import React, { useState } from 'react';
import { Calculator, Copy, Trash2 } from 'lucide-react';

const MarkupCalculatorTool = () => {
  const [cost, setCost] = useState('');
  const [markupPercent, setMarkupPercent] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const c = parseFloat(cost);
    const markup = parseFloat(markupPercent);

    if (isNaN(c) || isNaN(markup) || c <= 0) {
      setResult(null);
      return;
    }

    // Selling Price = Cost × (1 + Markup% / 100)
    const sellingPrice = c * (1 + markup / 100);
    const markupAmount = sellingPrice - c;
    const profitMargin = (markupAmount / sellingPrice) * 100;

    setResult({
      cost: c,
      markupPercent: markup,
      sellingPrice,
      markupAmount,
      profitMargin
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setCost('');
    setMarkupPercent('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Cost ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="e.g., 50"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="cost-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Markup (%)
            </label>
            <input
              type="number"
              step="0.1"
              value={markupPercent}
              onChange={(e) => setMarkupPercent(e.target.value)}
              placeholder="e.g., 40"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="markup-percent-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-markup-btn"
      >
        <Calculator size={18} />
        Calculate Selling Price
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="markup-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Selling Price</span>
              <button
                onClick={() => copyResult(result.sellingPrice)}
                className={`p-1.5 rounded transition-colors ${
                  copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-selling-price"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="text-3xl font-bold text-green-400" data-testid="selling-price-result">
              {formatCurrency(result.sellingPrice)}
            </div>
          </div>

          {/* Secondary Results */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Markup Amount</div>
              <div className="text-xl font-bold text-yellow-400">
                {formatCurrency(result.markupAmount)}
              </div>
            </div>
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Profit Margin</div>
              <div className="text-xl font-bold text-blue-400">
                {result.profitMargin.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500">of selling price</div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Cost</span>
                <span className="text-white font-medium">{formatCurrency(result.cost)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Markup</span>
                <span className="text-white font-medium">{result.markupPercent}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Markup Amount</span>
                <span className="text-yellow-400 font-medium">+{formatCurrency(result.markupAmount)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Selling Price</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.sellingPrice)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Markup Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          Selling Price = Cost × (1 + Markup% / 100)
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Common Markup Percentages</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Grocery</div>
            <div>15% - 25%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Clothing/Retail</div>
            <div>50% - 100%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Restaurants</div>
            <div>200% - 300%</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Markup vs Margin</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div><span className="text-white">Markup:</span> Percentage added to cost</div>
          <div><span className="text-white">Margin:</span> Percentage of selling price that is profit</div>
          <div className="text-xs text-gray-500 mt-2">Example: 50% markup = 33.3% profit margin</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-markup-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default MarkupCalculatorTool;
